import "./_style.less";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo } from "react";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import {
  FloatingBookingStep,
  FloatingBookingStepItem,
} from "../../../../components";
import BookingCounselingStepPsychologist from "../booking-counseling-step-psychologist";
import BookingCounselingStepSchedule from "../booking-counseling-step-schedule";
import BookingCounselingStepPreview from "../booking-counseling-step-preview";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../stores/hooks";
import { isValidEmail } from "../../../../@vendor/utils";
import { CountryPhoneInputValue } from "antd-country-phone-input";
import _ from "lodash";

const BookingCounselingModule = () => {
  const { t } = useTranslation();
  const {
    onPrevStep,
    onNextStep,
    currentStep,
    form,
    resetStep,
    resetForm,
    isPatientEmailValid,
    isPatientPhoneValid,
  } = useBookingCounseling();
  const [searchParams, setSearchParams] = useSearchParams();
  const { values, setFieldValue } = form;
  const { account } = useAppSelector((state) => state.system);
  const isLogin = useMemo(() => !!account?.id, [account]);

  const isValidPhone = useCallback((phoneValue: CountryPhoneInputValue) => {
    const phoneLength = _.get(phoneValue, "phone.length", 0);
    return phoneLength >= 7 && phoneLength <= 15;
  }, []);

  const handleOnNextStep = () => {
    const warn = (text: string) => message.warning(text).then();

    const isSingleSession = values?.session === "single-session";

    if (currentStep === 0) {
      if (!values?.psychologist_id || !values?.category_id) {
        return warn(t("notification.error.pleaseCompletePsychologist"));
      }
    }

    // Step 1: Schedule & Patient Validation
    if (currentStep === 1) {
      const isCustomerValid = isLogin
        ? !!values?.customer_id
        : !!values?.patient?.phone_number?.phone &&
        !!values?.patient?.email &&
        !!values?.patient?.name &&
        !!values?.patient?.gender &&
        !!values?.patient?.date_of_birth;

      const isScheduleValid = isSingleSession
        ? !!values?.schedule_id &&
        !!values?.schedule_duration &&
        !!values?.schedule_date
        : Array.isArray(values?.schedule_ids) &&
        values?.schedule_ids.length === 5;

      const isTypeValid = !!values?.type;

      if (!isTypeValid) {
        return warn(t("notification.error.pleaseCompleteTheForm"));
      }

      if (!isScheduleValid) {
        return warn(t("notification.error.pleaseCompleteSchedule"));
      }

      if (isLogin) {
        if (!isCustomerValid) {
          return warn(
            t("notification.error.selectItem", {
              item: t("common.text.patient"),
            })
          );
        }
      } else {
        if (values?.patient?.email && values?.patient?.phone_number?.phone) {
          if (!isPatientPhoneValid && !isPatientEmailValid) {
            return warn(t("common.text.emailPhoneAlreadyUsed"));
          }
        }

        if (values?.patient?.email) {
          if (isValidEmail(values?.patient?.email)) {
            if (!isPatientEmailValid) {
              return warn(t("common.text.emailAlreadyUsed"));
            }
          } else {
            return warn(
              t("validation.type.email", {
                item: t("common.text.patientEmail"),
              })
            );
          }
        }

        if (values?.patient?.phone_number?.phone) {
          if (isValidPhone(values?.patient?.phone_number)) {
            if (!isPatientPhoneValid) {
              return warn(t("common.text.phoneAlreadyUsed"));
            }
          } else {
            return warn(
              t("validation.invalid", {
                item: t("common.text.patientPhoneNumber"),
              })
            );
          }
        }

        if (!isCustomerValid) {
          return warn(t("notification.error.pleaseCompleteTheForm"));
        }
      }
    }

    onNextStep();
  };

  useEffect(() => {
    if (searchParams.get("psychologist")) {
      setFieldValue(
        "psychologist_id",
        Number(searchParams.get("psychologist"))
      );
      searchParams.delete("psychologist");
      setSearchParams(searchParams);
    }
  }, [searchParams.get("psychologist")]);

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (!localStorage.getItem("redirectToBooking")) {
          resetForm();
          resetStep();
        }
      }, 500);
    };
  }, []);

  return (
    <div id="booking-counseling-wrapper">
      <div className="container">
        <div className="book-counseling-title">
          {t("common.text.bookCounselingAndPsychotherapy")}
        </div>

        <div style={{ paddingTop: 24 }}>
          <BookingCounselingStepPsychologist />
          <BookingCounselingStepSchedule />
          <BookingCounselingStepPreview />
        </div>
      </div>

      <FloatingBookingStep
        previewContent={
          <>
            <FloatingBookingStepItem
              label={t("common.text.psychologist")}
              value={0}
              currentStep={currentStep}
              total={3}
            />
            <FloatingBookingStepItem
              label={t("common.text.schedule")}
              value={1}
              currentStep={currentStep}
              total={3}
            />
            <FloatingBookingStepItem
              label={t("common.text.preview")}
              value={2}
              currentStep={currentStep}
              total={3}
            />
          </>
        }
        isLastItem={currentStep === 2}
        isFirstItem={currentStep === 0}
        onBack={onPrevStep}
        onNext={handleOnNextStep}
      />
    </div>
  );
};

export default BookingCounselingModule;
